"use client";
import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import {
  PaperClipIcon,
  PaperAirplaneIcon,
  DocumentTextIcon,
  LinkIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

export type ChatInputAttachment = {
  id: string;
  label: string;
  kind?: 'artifact' | 'document';
  onRemove?: () => void;
};

export interface ChatInputV2Props extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  onSend?: (message: string) => void;
  onValueChange?: (value: string) => void;
  attachments?: ChatInputAttachment[];
  onAttachmentClick?: () => void;
  attachmentButtonLabel?: string;
}

export default function ChatInputV2({
  onSend,
  onValueChange,
  attachments = [],
  onAttachmentClick,
  attachmentButtonLabel = 'Add attachment',
  className,
  value,
  onChange,
  ...props
}: ChatInputV2Props) {
  const [internalValue, setInternalValue] = useState('');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const isControlled = value !== undefined;
  const currentValue = isControlled ? String(value ?? '') : internalValue;

  // Auto-expand textarea up to 160px
  useEffect(() => {
    const el = textAreaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }, [currentValue]);

  const canSend = currentValue.trim().length > 0;
  const charCount = currentValue.length;

  const updateValue = (nextValue: string, event?: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!isControlled) {
      setInternalValue(nextValue);
    }
    onValueChange?.(nextValue);
    if (event) {
      onChange?.(event);
    }
  };

  const handleSend = () => {
    const trimmed = currentValue.trim();
    if (!trimmed) return;
    onSend?.(trimmed);
    updateValue('');
    if (textAreaRef.current) textAreaRef.current.style.height = 'auto';
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.key === 'Enter' && !e.shiftKey) || ((e.metaKey || e.ctrlKey) && e.key === 'Enter')) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className={cn(
        'flex-shrink-0 border-t border-border/30',
        'bg-background/85 backdrop-blur-md',
        'p-3',
        className
      )}
    >
      {attachments.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {attachments.map((attachment) => (
            <span
              key={attachment.id}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border/50 bg-muted/30 px-2.5 py-1 text-xs text-foreground"
            >
              {attachment.kind === 'artifact' ? (
                <DocumentTextIcon className="h-3.5 w-3.5 text-primary" />
              ) : (
                <LinkIcon className="h-3.5 w-3.5 text-info" />
              )}
              <span className="truncate">{attachment.label}</span>
              {attachment.onRemove && (
                <button
                  type="button"
                  className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  onClick={attachment.onRemove}
                  aria-label={`Remove ${attachment.label}`}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <div
        className={cn(
          'relative flex items-end gap-2 p-3',
          'bg-background/50 backdrop-blur-sm',
          'border border-border/40 rounded-2xl',
          'focus-within:border-primary/60 focus-within:shadow-lg focus-within:shadow-primary/20',
          'transition-all duration-200'
        )}
      >
          {/* Leading actions */}
          <div className="flex items-center gap-1 pl-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="min-w-[44px] min-h-[44px]"
              aria-label={attachmentButtonLabel}
              onClick={onAttachmentClick}
            >
              <PaperClipIcon className="h-5 w-5" />
            </Button>
          </div>

          {/* Textarea */}
          <textarea
            ref={textAreaRef}
            className={cn(
              'flex-1 resize-none bg-transparent px-4 py-3',
              'text-sm text-foreground placeholder:text-muted-foreground',
              'border-0 outline-none focus:outline-none focus:ring-0 focus-visible:ring-0',
              'max-h-[160px] min-h-[56px]'
            )}
            rows={1}
            placeholder="Type your message..."
            value={currentValue}
            onChange={(e) => updateValue(e.target.value, e)}
            onKeyDown={onKeyDown}
            data-testid="chat-input"
            {...props}
          />

          {/* Trailing: char count + send */}
          <div className="flex items-center gap-2 pr-1">
            {charCount > 0 && (
              <span className="text-xs text-muted-foreground/60 tabular-nums select-none">
                {charCount}
              </span>
            )}
            <Button
              variant={canSend ? 'glow' : 'ghost'}
              size="icon"
              className={cn('rounded-full h-12 w-12 min-w-[48px] min-h-[48px]')}
              onClick={handleSend}
              disabled={!canSend}
              aria-label="Send message"
              data-testid="send-button"
            >
              <PaperAirplaneIcon className={cn('h-5 w-5 transition-transform duration-200', canSend && '-rotate-45')} />
            </Button>
          </div>
        </div>
      {/* Helper text hidden to save vertical space */}
      <p className="hidden">
        Press Enter to send &middot; Shift+Enter for newline
      </p>
    </div>
  );
}
